import { Controller } from "@hotwired/stimulus";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

export default class extends Controller {
  connect() {
    this.lightbox = new PhotoSwipeLightbox({
      gallery: this.element,
      children: "a[data-pswp-width]",
      pswpModule: () => import("photoswipe"),
      bgOpacity: 0.9,
      bgClickAction: "close",
      tapAction: "close",
      paddingFn: (viewportSize) => {
        if (viewportSize.x > 800) {
          return { top: 80, bottom: 80, left: 100, right: 100 };
        }
        return { top: 20, bottom: 80, left: 0, right: 0 };
      },
    });

    this.#registerVideoContentType();
    this.#pauseVideosOnClose();
    this.#registerDownloadButton();

    this.lightbox.init();
  }

  disconnect() {
    this.lightbox?.destroy();
    this.lightbox = null;
  }

  // Custom content type for Bunny CDN video iframes
  #registerVideoContentType() {
    this.lightbox.addFilter("itemData", (itemData) => {
      const videoUrl = itemData.element?.dataset?.pswpVideoUrl;
      if (videoUrl) {
        itemData.videoUrl = videoUrl;
        itemData.videoDownloadUrl =
          itemData.element?.dataset?.pswpVideoDownloadUrl;
        itemData.type = "video";
      }
      return itemData;
    });

    this.lightbox.on("contentLoad", (e) => {
      const { content } = e;
      if (content.data.videoUrl) {
        e.preventDefault();

        content.element = document.createElement("div");
        content.element.className = "pswp__video-container";

        const iframe = document.createElement("iframe");
        iframe.setAttribute("allowfullscreen", "");
        iframe.setAttribute("allow", "autoplay; encrypted-media");
        iframe.setAttribute("frameborder", "0");
        iframe.src = content.data.videoUrl;
        content.element.appendChild(iframe);
      }
    });

    this.lightbox.on("contentAppend", (e) => {
      const { content } = e;
      if (content.data.videoUrl && !content.element.parentNode) {
        e.preventDefault();
        content.slide.container.appendChild(content.element);
      }
    });

    this.lightbox.on("contentRemove", (e) => {
      const { content } = e;
      if (content.data.videoUrl && content.element?.parentNode) {
        e.preventDefault();
        content.element.remove();
      }
    });
  }

  // Stop video playback when lightbox closes or slide changes
  #pauseVideosOnClose() {
    const clearIframes = (container) => {
      container?.querySelectorAll("iframe").forEach((iframe) => {
        iframe.src = "";
      });
    };

    this.lightbox.on("close", () => {
      clearIframes(this.lightbox.pswp?.element);
    });

    this.lightbox.on("contentDeactivate", ({ content }) => {
      if (content.data.videoUrl && content.element) {
        const iframe = content.element.querySelector("iframe");
        if (iframe) {
          iframe.removeAttribute("src");
        }
      }
    });

    this.lightbox.on("contentActivate", ({ content }) => {
      if (content.data.videoUrl && content.element) {
        const iframe = content.element.querySelector("iframe");
        if (iframe && !iframe.getAttribute("src")) {
          iframe.src = content.data.videoUrl;
        }
      }
    });
  }

  #registerDownloadButton() {
    this.lightbox.on("uiRegister", () => {
      this.lightbox.pswp.ui.registerElement({
        name: "download-button",
        order: 8,
        isButton: true,
        html: {
          isCustomSVG: true,
          inner:
            '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 15.7l6 6.3 6-6.3-1.5-1.4ZM23 23H9v2h14" id="pswp__icn-download"/>',
          outlineID: "pswp__icn-download",
        },
        onInit: (el, pswp) => {
          el.addEventListener("click", (e) => {
            e.preventDefault();
            const data = pswp.currSlide.data;
            const url = data.videoDownloadUrl || data.src;
            if (!url) return;

            fetch(url)
              .then((res) => res.blob())
              .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = url.split("/").pop() || "download";
                a.click();
                URL.revokeObjectURL(a.href);
              });
          });

          pswp.on("change", () => {
            const data = pswp.currSlide.data;
            el.style.display = data.videoDownloadUrl || data.src ? "" : "none";
          });
        },
      });
    });
  }
}
